import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'

import type { ActivationGroup } from '@/features/activationGroup/types'
import ReviewBidsTable from '@/features/bidding/components/cascading/review/ReviewBidsTable'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidDirection } from '@/features/bidding/types/bidLadderInput'
import type { ValidationError } from '@/features/bidding/utils/cascading/bidLadder/transformBidLevelsToExtendedBids'
import { wattHoursToMegawattHours } from '@/utils/powerEnergyTransformations'

type DirectionBidsSectionProps = {
  direction: BidDirection
  /** Bids already filtered by direction */
  bids: ExtendedBid[]
  /** All validation errors - will be filtered to this direction's bids */
  allErrors: ValidationError[]
  activationGroupsMap: Map<string, ActivationGroup>
}

type MarketSummary = {
  portfolios: Set<string>
  activationGroups: number
  totalPtus: number
  minPrice: number
  maxPrice: number
  totalVolumeMW: number
}

function calculateMarketSummary(bids: ExtendedBid[], activationGroupsMap: Map<string, ActivationGroup>): MarketSummary {
  if (bids.length === 0) {
    return {
      portfolios: new Set<string>(),
      activationGroups: 0,
      totalPtus: 0,
      minPrice: 0,
      maxPrice: 0,
      totalVolumeMW: 0,
    }
  }

  const portfolios = new Set<string>()
  let minPrice = Number.POSITIVE_INFINITY
  let maxPrice = Number.NEGATIVE_INFINITY
  let totalVolumeWh = 0
  let totalPtus = 0

  for (const bid of bids) {
    const activationGroup = activationGroupsMap.get(bid.activationGroupUuid)
    if (activationGroup?.portfolio?.code) {
      portfolios.add(activationGroup.portfolio.code)
    }

    for (const ptu of bid.ptus) {
      const price = ptu.ptuChunks?.[0]?.offeredPrice ?? 0
      if (price < minPrice) minPrice = price
      if (price > maxPrice) maxPrice = price
      totalVolumeWh += ptu.offeredVolume.quantity
      totalPtus++
    }
  }

  return {
    portfolios,
    activationGroups: bids.length,
    totalPtus,
    minPrice: minPrice === Number.POSITIVE_INFINITY ? 0 : minPrice,
    maxPrice: maxPrice === Number.NEGATIVE_INFINITY ? 0 : maxPrice,
    totalVolumeMW: wattHoursToMegawattHours(totalVolumeWh),
  }
}

function DirectionBidsSection({
  direction,
  bids,
  allErrors,
  activationGroupsMap,
}: Readonly<DirectionBidsSectionProps>) {
  // Filter errors to only those matching this direction's bids
  const errors = useMemo(
    () => allErrors.filter((e) => bids.some((b) => b.activationGroupUuid === e.groupUuid)),
    [allErrors, bids],
  )

  // Calculate summary for this direction
  const summary = useMemo(() => calculateMarketSummary(bids, activationGroupsMap), [bids, activationGroupsMap])

  const priceRangeDisplay =
    summary.minPrice === summary.maxPrice
      ? `${summary.minPrice.toFixed(2)}`
      : `${summary.minPrice.toFixed(2)}–${summary.maxPrice.toFixed(2)}`

  const isUpDirection = direction === 'up'

  return (
    <>
      {/* Compact summary stats bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          px: 3,
          py: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            backgroundColor: isUpDirection ? 'success.lighter' : 'error.lighter',
            borderRadius: 1,
            border: '1px solid',
            borderColor: isUpDirection ? 'success.light' : 'error.light',
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
            {Array.from(summary.portfolios).join(', ') || '—'}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            •
          </Typography>
          <Typography sx={{ fontSize: '0.75rem' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {summary.activationGroups}
            </Box>{' '}
            groups
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            •
          </Typography>
          <Typography sx={{ fontSize: '0.75rem' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {summary.totalPtus}
            </Box>{' '}
            PTUs
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            •
          </Typography>
          <Typography sx={{ fontSize: '0.75rem' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {priceRangeDisplay}
            </Box>{' '}
            EUR
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            •
          </Typography>
          <Typography sx={{ fontSize: '0.75rem' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {summary.totalVolumeMW.toFixed(1)}
            </Box>{' '}
            MWh
          </Typography>
        </Box>
      </Box>

      {/* Review Table */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', px: 0 }}>
        <ReviewBidsTable activationGroupsMap={activationGroupsMap} bids={bids} errors={errors} />
      </Box>
    </>
  )
}

export default DirectionBidsSection
